import React, { useEffect, useState } from 'react';

import { ACTION } from '@atlaskit/editor-common/analytics/types/enums';
import { createUnifiedAnalyticsPayload } from '@atlassian/editor-ai-common/analytics/create-unified-analytics-payload';
import { NoImprovementScreen } from '@atlassian/generative-ai-modal/screens/NoImprovement';

import type { StreamFns } from '../../../../pm-plugins/in-editor-direct-streaming/hooks/useInEditorDirectStreaming';
import { useAIExperienceCommonDataContext } from '../../../hooks/useAIExperienceCommonData';
import { convertExperienceName } from '../../../utils/analytics/analyticsFlow';
import { useAnalyticsFlow } from '../../../utils/analytics/analyticsFlowUtils';
import { useFireAIAnalyticsEvent } from '../../../utils/analytics/utils';
import type { AIExperienceMachineContext } from '../../../utils/xstate/get-ai-experience-service';

/**
 * No Improvement Screen with Logic
 * This screen is shown when the AI model does not provide any improvement
 * to the user's input.
 */
export function NoImprovementScreenWithLogic({
	discardStream,
	aiExperienceMachineContext,
}: {
	aiExperienceMachineContext: AIExperienceMachineContext;
	discardStream: StreamFns['discardStream'];
}): React.JSX.Element {
	const aiExperienceCommonData = useAIExperienceCommonDataContext();
	const { endExperience, editorView, appearance, lastTriggeredFrom } = aiExperienceCommonData;

	const { channelId, configItem } = aiExperienceMachineContext;

	const [isHidden, setIsHidden] = useState(false);

	const analyticsFlow = useAnalyticsFlow();
	const fireAIAnalyticsEvent = useFireAIAnalyticsEvent();

	useEffect(() => {
		// We must fire a viewed event to ensure the AI interaction is counted toward MAU stats. Even though nothing has changed on
		// screen we still count this as the user having seen the result.
		fireAIAnalyticsEvent({
			payload: createUnifiedAnalyticsPayload({
				action: ACTION.VIEWED,
				interactionID: channelId ?? analyticsFlow.getLastAiInteractionId(),
				experienceName: convertExperienceName(configItem.key.toString()),
				isProactive: false,
				attributes: analyticsFlow.getAiLifeCycleDynamicAttributes({ init: false }),
				invokedFrom: lastTriggeredFrom,
				traceIds: analyticsFlow.getCommonAttributes().traceIds,
				promptVersion: analyticsFlow.getCommonAttributes().promptVersion,
			}),
		});

		discardStream?.({ restoreSelection: false });

		const timer = setTimeout(() => {
			setIsHidden(true);
		}, 1000);

		return () => clearTimeout(timer);
	}, [
		editorView,
		discardStream,
		channelId,
		configItem,
		lastTriggeredFrom,
		analyticsFlow,
		fireAIAnalyticsEvent,
	]);

	// TODO: EDITOR-1085 - Add analytics for no improvement screen
	return (
		<NoImprovementScreen isHidden={isHidden} onceFade={endExperience} appearance={appearance} />
	);
}
